import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Download, Edit3, Plus, Search, Trash2, UploadCloud } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import type { Product } from "../lib/types";
import { dateTime, money, platformLabel, statusLabel } from "../lib/format";
import { StatusBadge, statusTone } from "../components/StatusBadge";
import { useI18n } from "../lib/i18n";

const statusTabs = [
  { value: "", label: "全部商品" },
  { value: "active", label: "在售中" },
  { value: "draft", label: "草稿" },
  { value: "archived", label: "已归档" }
];

export function ProductsPage() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { t } = useI18n();
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [selected, setSelected] = useState<string[]>([]);
  const query = useQuery({
    queryKey: ["catalog-products", search, status],
    queryFn: () => {
      const params = new URLSearchParams();
      if (search) params.set("search", search);
      if (status) params.set("status", status);
      return api.get<{ products: Product[] }>(`/api/catalog/products?${params.toString()}`);
    }
  });
  const archive = useMutation({
    mutationFn: (id: string) => api.delete(`/api/catalog/products/${id}`),
    onSuccess: async () => queryClient.invalidateQueries({ queryKey: ["catalog-products"] })
  });

  const products = query.data?.products ?? [];
  const allSelected = products.length > 0 && products.every((product) => selected.includes(product.id));
  const primaryImage = (product: Product) =>
    product.images?.find((image) => image.is_primary === 1)?.url ?? product.images?.[0]?.url ?? "";
  const totalStock = (product: Product) =>
    product.variants?.reduce((sum, variant) => sum + variant.stock, 0) ?? 0;
  const minPrice = (product: Product) => {
    const prices = (product.variants ?? []).map((variant) => variant.price).filter((price) => price > 0);
    return prices.length > 0 ? Math.min(...prices) : 0;
  };

  return (
    <div className="page-stack">
      <div className="toolbar-row">
        <div className="filter-row">
          <label className="search-box compact">
            <Search size={15} />
            <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder={t("搜索 SKU、SPU 或商品名称")} />
          </label>
          <div className="status-tabs">
            {statusTabs.map((item) => (
              <button
                key={item.value}
                type="button"
                className={status === item.value ? "status-tab active" : "status-tab"}
                onClick={() => setStatus(item.value)}
              >
                {t(item.label)}
              </button>
            ))}
          </div>
        </div>
        <div className="button-row">
          <button type="button" className="ghost-button" onClick={() => navigate("/collection")}>
            <Download size={16} />
            {t("产品采集")}
          </button>
          <button type="button" className="primary-button" onClick={() => navigate("/products/new")}>
            <Plus size={16} />
            {t("创建产品")}
          </button>
        </div>
      </div>

      <div className="panel table-panel">
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={(event) => setSelected(event.target.checked ? products.map((product) => product.id) : [])}
                  />
                </th>
                <th>{t("产品信息")}</th>
                <th>SKU</th>
                <th>{t("状态")}</th>
                <th>{t("售价")}</th>
                <th>{t("库存")}</th>
                <th>{t("刊登")}</th>
                <th>{t("更新时间")}</th>
                <th>{t("操作")}</th>
              </tr>
            </thead>
            <tbody>
              {products.map((product) => (
                <tr key={product.id}>
                  <td>
                    <input
                      type="checkbox"
                      checked={selected.includes(product.id)}
                      onChange={(event) =>
                        setSelected((current) =>
                          event.target.checked ? [...current, product.id] : current.filter((id) => id !== product.id)
                        )
                      }
                    />
                  </td>
                  <td>
                    <div className="product-cell">
                      <div className="product-thumb">
                        {primaryImage(product) ? <img src={primaryImage(product)} alt={product.name} /> : <UploadCloud size={20} />}
                      </div>
                      <div>
                        <strong>{product.name}</strong>
                        <span>{product.category || t("未设置类目")} · {product.brand || t("无品牌")}</span>
                        <small>SPU: {product.spu}</small>
                      </div>
                    </div>
                  </td>
                  <td className="mono">{product.variants?.[0]?.sku ?? product.sku}</td>
                  <td><StatusBadge tone={statusTone(product.status)}>{t(statusLabel(product.status))}</StatusBadge></td>
                  <td>{minPrice(product) > 0 ? money(minPrice(product), product.currency) : "-"}</td>
                  <td>{totalStock(product)}</td>
                  <td>
                    <div className="listing-dots">
                      {product.listings?.length ? (
                        product.listings.map((listing) => (
                          <span key={listing.id} className={`platform-dot dot-${listing.platform ?? "amazon"}`} title={`${listing.external_listing_id} ${listing.status}`} />
                        ))
                      ) : (
                        <span className="muted">{t("未刊登")}</span>
                      )}
                    </div>
                  </td>
                  <td>{dateTime(product.updated_at)}</td>
                  <td>
                    <div className="button-row">
                      <Link className="ghost-button compact-button" to={`/products/${product.id}`}>
                        <Edit3 size={15} />
                        {t("编辑")}
                      </Link>
                      <button
                        type="button"
                        className="icon-button danger-button"
                        onClick={() => archive.mutate(product.id)}
                        aria-label={t("归档")}
                      >
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {products.length === 0 ? (
                <tr>
                  <td colSpan={9} className="empty-table">{t("暂无商品，先采集或创建一个产品。")}</td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
